import axios, { AxiosError } from "axios";
import { BASE_URL } from "../utils/config";
import { deleteSessionCookie, getSessionCookie, setSessionCookie } from "../utils/cookies";

export const instance = axios.create({
  baseURL: BASE_URL,
});

// ─── Refresh Token Queue ───────────────────────────────────────────────────────
let isRefreshing = false;
let failedQueue: { resolve: (token: string) => void; reject: (error: any) => void }[] = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((promise) => {
    if (error) {
      promise.reject(error);
    } else {
      promise.resolve(token!);
    }
  });
  failedQueue = [];
};

const clearSessionAndRedirect = () => {
  deleteSessionCookie("accessToken");
  deleteSessionCookie("refreshToken");
  if (window.location.pathname !== "/login") {
    window.location.href = "/login";
  }
};

// ─── Request Interceptor ───────────────────────────────────────────────────────
instance.interceptors.request.use(
  (config) => {
    const accessToken = getSessionCookie("accessToken");
    config.headers = config.headers || {};
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    config.headers["ngrok-skip-browser-warning"] = "any";
    return config;
  },
  (err) => Promise.reject(err)
);

// ─── Response Interceptor ──────────────────────────────────────────────────────
instance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as any;

    if (error?.response?.status !== 401) {
      return Promise.reject(error);
    }

    if (originalRequest._retry) {
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      })
        .then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return instance(originalRequest);
        })
        .catch((err) => Promise.reject(err));
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      const refreshToken = getSessionCookie("refreshToken");
      console.log("Attempting token refresh with refresh token: 2", refreshToken);
      if (!refreshToken) {
        console.log("unable to get token")
        processQueue(error, null);
        clearSessionAndRedirect();
        return Promise.reject(error);
      }

      const res = await axios.post(
        `${BASE_URL}/auth/refresh`,
        {},
        {
          headers: {
            Authorization: `Bearer ${refreshToken}`,
          },
        }
      );

      if (res?.data?.accessToken) {
        setSessionCookie("accessToken", res.data.accessToken);
        setSessionCookie("refreshToken", res.data.refreshToken);
        originalRequest.headers.Authorization = `Bearer ${res.data.accessToken}`;
        processQueue(null, res.data.accessToken);
        return instance(originalRequest);
      } else {
        processQueue(error, null);
        clearSessionAndRedirect();
        return Promise.reject(error);
      }
    } catch (refreshError) {
      processQueue(refreshError, null);
      clearSessionAndRedirect();
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);

// ─── Response Type ─────────────────────────────────────────────────────────────
type IResponse<D> =
  | { data: D; success: true }
  | { success: false; error: any; statusCode: number };

// ─── API Manager ───────────────────────────────────────────────────────────────
export const apiManager = {
  get: async <R = any>(
    endpoint: string,
    params: Record<string, any> = {}
  ): Promise<IResponse<R>> => {
    try {
      const response = await instance.get<R>(endpoint, { params });
      return { data: response.data, success: true };
    } catch (error: any) {
      return {
        success: false,
        error: error?.response?.data || error.message,
        statusCode: error?.response?.status || 500,
      };
    }
  },

  post: async <R = any>(
    endpoint: string,
    body?: any,
    params: Record<string, any> = {}
  ): Promise<IResponse<R>> => {
    try {
      const response = await instance.post<R>(endpoint, body, { params });
      return { data: response.data, success: true };
    } catch (error: any) {
      return {
        success: false,
        error: error?.response?.data || error.message,
        statusCode: error?.response?.status || 500,
      };
    }
  },

  put: async <R = any>(
    endpoint: string,
    body?: any,
    params: Record<string, any> = {}
  ): Promise<IResponse<R>> => {
    try {
      const response = await instance.put<R>(endpoint, body, { params });
      return { data: response.data, success: true };
    } catch (error: any) {
      return {
        success: false,
        error: error?.response?.data || error.message,
        statusCode: error?.response?.status || 500,
      };
    }
  },

  patch: async <R = any>(
    endpoint: string,
    body?: any,
    params: Record<string, any> = {},
    headers: Record<string, any> = {}
  ): Promise<IResponse<R>> => {
    try {
      const response = await instance.patch<R>(endpoint, body, { params, headers });
      return { data: response.data, success: true };
    } catch (error: any) {
      return {
        success: false,
        error: error?.response?.data || error.message,
        statusCode: error?.response?.status || 500,
      };
    }
  },

  delete: async <R = any>(
    endpoint: string,
    params: Record<string, any> = {}
  ): Promise<IResponse<R>> => {
    try {
      const response = await instance.delete<R>(endpoint, { params });
      return { data: response.data, success: true };
    } catch (error: any) {
      return {
        success: false,
        error: error?.response?.data || error.message,
        statusCode: error?.response?.status || 500,
      };
    }
  },
};